import React from 'react';
import { getShopOptionCount } from '../config/runSettings';
import { GAME_CONFIG } from '../config/gameConfig';
import {
  collectGrantedGameEffectsFromTiles,
  normalizeActiveGameEffects,
} from '../engine/scoringEffects';
import { drawShopOptions } from '../engine/shop';
import { drawTiles } from '../engine/tileBag';
import {
  playDiscardConfirmSound,
  playDiscardMarkSound,
  playDiscardModeEnterSound,
  playSubmitSuccessSound,
  playTilePlaceSound,
  triggerDiscardConfirmHaptic,
  triggerDiscardMarkHaptic,
  triggerDiscardModeEnterHaptic,
} from '../services/audioService';
import {
  trackDiscardStats,
  trackShopVisit,
  trackSubmitStats,
  trackVictory,
} from '../services/playerStatsTrackingService';
import {
  getShopPicksWithEffects,
  getShopRarityChancesWithEffects,
  getShopRerollsWithEffects,
  getRoundDiscardsWithEffects,
  getRoundSubmitsWithEffects,
} from './scrabbleGameHelpers';


export const useRoundLoop = ({
  state,
  setState,
  evaluation,
  wordsWithValidation,
  dictionaryStatus,
  canSubmit,
  setSubmitMessage,
}) => {
  const placeRackTile = React.useCallback((tileId, squareTileId) => {
    setState((prev) => {
      if (prev.gamePhase !== 'playing' || prev.isDiscardSelecting) {
        return prev;
      }

      const tile = prev.rackTiles.find((entry) => entry.id === tileId);
      if (!tile) {
        return prev;
      }

      if (prev.committedBoard[squareTileId]) {
        return prev;
      }

      const existingStaged = prev.stagedBoard[squareTileId];
      const nextRack = prev.rackTiles.filter((entry) => entry.id !== tileId);

      if (existingStaged) {
        nextRack.push(existingStaged);
      }

      playTilePlaceSound();

      return {
        ...prev,
        stagedBoard: { ...prev.stagedBoard, [squareTileId]: tile },
        rackTiles: nextRack,
      };
    });
  }, [setState]);

  const moveStagedTile = React.useCallback((fromTileId, toTileId) => {
    setState((prev) => {
      if (prev.gamePhase !== 'playing' || prev.isDiscardSelecting) {
        return prev;
      }

      if (prev.committedBoard[toTileId]) {
        return prev;
      }

      const moving = prev.stagedBoard[fromTileId];
      if (!moving || fromTileId === toTileId) {
        return prev;
      }

      const nextStaged = { ...prev.stagedBoard };
      delete nextStaged[fromTileId];

      const displaced = nextStaged[toTileId];
      if (displaced) {
        nextStaged[fromTileId] = displaced;
      }

      nextStaged[toTileId] = moving;
      playTilePlaceSound();

      return { ...prev, stagedBoard: nextStaged };
    });
  }, [setState]);

  const returnStagedTileToRack = React.useCallback((squareTileId) => {
    setState((prev) => {
      if (prev.gamePhase !== 'playing' || prev.isDiscardSelecting) {
        return prev;
      }

      const staged = prev.stagedBoard[squareTileId];
      if (!staged) {
        return prev;
      }

      const nextStaged = { ...prev.stagedBoard };
      delete nextStaged[squareTileId];

      return {
        ...prev,
        stagedBoard: nextStaged,
        rackTiles: [...prev.rackTiles, staged],
      };
    });
  }, [setState]);

  const returnAllStagedTiles = React.useCallback(() => {
    setState((prev) => {
      if (prev.gamePhase !== 'playing' || prev.isDiscardSelecting) {
        return prev;
      }

      const stagedTiles = Object.values(prev.stagedBoard);
      if (!stagedTiles.length) {
        return prev;
      }

      return {
        ...prev,
        stagedBoard: {},
        rackTiles: [...prev.rackTiles, ...stagedTiles],
      };
    });
  }, [setState]);

  const reorderRackTile = React.useCallback((tileId, targetIndex) => {
    setState((prev) => {
      if (prev.gamePhase !== 'playing' || prev.isDiscardSelecting) {
        return prev;
      }

      const currentIndex = prev.rackTiles.findIndex((tile) => tile.id === tileId);
      if (currentIndex < 0) {
        return prev;
      }

      const boundedTargetIndex = Math.max(0, Math.min(Number(targetIndex) || 0, prev.rackTiles.length - 1));
      if (currentIndex === boundedTargetIndex) {
        return prev;
      }

      const nextRackTiles = [...prev.rackTiles];
      const [movingTile] = nextRackTiles.splice(currentIndex, 1);
      nextRackTiles.splice(boundedTargetIndex, 0, movingTile);

      return { ...prev, rackTiles: nextRackTiles };
    });
  }, [setState]);

  const submitMove = React.useCallback(() => {
    if (state.gamePhase !== 'playing') {
      setSubmitMessage('Finish your shop pick before submitting a move.');
      return false;
    }

    if (dictionaryStatus === 'error') {
      setSubmitMessage('Dictionary unavailable. Submit is blocked.');
      return false;
    }

    if (state.submitsRemaining <= 0) {
      setSubmitMessage('No submits left this round.');
      return false;
    }

    if (state.isDiscardSelecting) {
      setSubmitMessage('Confirm or cancel your discard selection first.');
      return false;
    }

    if (!canSubmit) {
      const fallback = evaluation.errors[0] || 'Move is not valid yet.';
      setSubmitMessage(fallback);
      return false;
    }

    let reachedRoundTarget = false;
    let failedRound = false;
    let achievedVictory = false;
    let submitStatsPayload = null;
    const mainWord = evaluation.mainWord?.text ?? null;

    setState((prev) => {
      const usedTiles = Object.values(prev.stagedBoard);
      const committedBoard = { ...prev.committedBoard, ...prev.stagedBoard };
      const drawn = drawTiles(prev.bagTiles, usedTiles.length);

      const scoredWords = wordsWithValidation
        .filter((word) => word.cells.length >= GAME_CONFIG.minWordLength)
        .map((word) => ({ word: word.text, score: word.score }));

      const scoreGain = scoredWords.reduce((acc, item) => acc + item.score, 0);
      const nextScore = prev.score + scoreGain;
      const nextRunPoints = prev.runPoints + scoreGain;
      const grantedEffects = collectGrantedGameEffectsFromTiles(usedTiles, {
        runSettings: prev.runSettings,
      });
      const nextActiveGameEffects = normalizeActiveGameEffects([
        ...prev.activeGameEffects,
        ...grantedEffects,
      ]);
      const nextSubmitsRemaining = Math.max(0, prev.submitsRemaining - 1);
      const nextTilesPlacedThisRound = (prev.tilesPlacedThisRound || 0) + usedTiles.length;
      reachedRoundTarget = nextScore >= prev.roundTarget && nextTilesPlacedThisRound >= (prev.roundTileTarget || 0);
      failedRound = !reachedRoundTarget && nextSubmitsRemaining <= 0;
      achievedVictory = reachedRoundTarget && !prev.infiniteMode && prev.roundIndex >= GAME_CONFIG.maxRounds;

      const baseSubmits = getRoundSubmitsWithEffects(prev.runSettings, prev.activeGameEffects);
      const baseDiscards = getRoundDiscardsWithEffects(prev.runSettings, prev.activeGameEffects);

      submitStatsPayload = {
        scoreGain,
        scoredWords,
        nextScore,
        nextRunPoints,
        roundIndex: prev.roundIndex,
        roundTarget: prev.roundTarget,
        roundTileTarget: prev.roundTileTarget,
        tilesPlacedThisRound: nextTilesPlacedThisRound,
        reachedRoundTarget,
        failedRound,
        achievedVictory,
        tilesPlaced: usedTiles.length,
        mainWord,
        submitsRemainingAfter: nextSubmitsRemaining,
        submitsUsed: baseSubmits - nextSubmitsRemaining,
        discardsUsed: baseDiscards - prev.discardsRemaining,
      };

      playSubmitSuccessSound();

      const nextGamePhase = achievedVictory
        ? 'victory'
        : reachedRoundTarget
          ? 'round-complete'
          : failedRound
            ? 'game-over'
            : prev.gamePhase;

      return {
        ...prev,
        committedBoard,
        stagedBoard: {},
        rackTiles: [...prev.rackTiles, ...drawn.drawn],
        bagTiles: drawn.bag,
        runPoints: nextRunPoints,
        score: nextScore,
        history: [...prev.history, ...scoredWords],
        activeGameEffects: nextActiveGameEffects,
        submitsRemaining: nextSubmitsRemaining,
        tilesPlacedThisRound: nextTilesPlacedThisRound,
        isDiscardSelecting: false,
        discardSelectionTileIds: [],
        gamePhase: nextGamePhase,
        shopPicksRemaining: 0,
        shopRerollsRemaining: 0,
        shopOptions: [],
        selectedShopOptionId: null,
      };
    });

    if (achievedVictory) {
      setSubmitMessage(`Round ${GAME_CONFIG.maxRounds} complete. You won!`);
    } else if (reachedRoundTarget) {
      setSubmitMessage('Target reached. Continue to shop.');
    } else {
      setSubmitMessage('Move submitted.');
    }

    console.log('submitMove payload', submitStatsPayload);
    trackSubmitStats(submitStatsPayload);
    if (achievedVictory) {
      trackVictory(submitStatsPayload);
    }
    return true;
  }, [
    canSubmit,
    dictionaryStatus,
    evaluation.errors,
    evaluation.mainWord,
    wordsWithValidation,
    state.gamePhase,
    state.submitsRemaining,
    state.isDiscardSelecting,
    setState,
    setSubmitMessage,
  ]);

  const startDiscardSelection = React.useCallback(() => {
    let modeEntered = false;

    setState((prev) => {
      if (prev.gamePhase !== 'playing' || prev.discardsRemaining <= 0 || !prev.rackTiles.length) {
        return prev;
      }

      modeEntered = true;
      return { ...prev, isDiscardSelecting: true, discardSelectionTileIds: [] };
    });

    if (state.gamePhase !== 'playing') {
      setSubmitMessage('Discard is only available while playing.');
      return;
    }

    if (state.discardsRemaining <= 0) {
      setSubmitMessage('No discards left this round.');
      return;
    }

    if (modeEntered) {
      playDiscardModeEnterSound();
      triggerDiscardModeEnterHaptic();
    }

    setSubmitMessage('Select rack tiles to discard.');
  }, [state.gamePhase, state.discardsRemaining, setState, setSubmitMessage]);

  const cancelDiscardSelection = React.useCallback(() => {
    setState((prev) => ({ ...prev, isDiscardSelecting: false, discardSelectionTileIds: [] }));
    setSubmitMessage('Discard selection canceled.');
  }, [setState, setSubmitMessage]);

  const toggleDiscardTileSelection = React.useCallback((tileId) => {
    let toggled = false;

    setState((prev) => {
      if (prev.gamePhase !== 'playing' || prev.discardsRemaining <= 0 || !prev.isDiscardSelecting) {
        return prev;
      }

      const hasRackTile = prev.rackTiles.some((tile) => tile.id === tileId);
      if (!hasRackTile) {
        return prev;
      }

      const selected = new Set(prev.discardSelectionTileIds);
      if (selected.has(tileId)) {
        selected.delete(tileId);
      } else {
        selected.add(tileId);
      }

      toggled = true;
      return { ...prev, discardSelectionTileIds: [...selected] };
    });

    if (toggled) {
      playDiscardMarkSound();
      triggerDiscardMarkHaptic();
    }
  }, [setState]);

  const confirmDiscardSelection = React.useCallback(() => {
    let discardApplied = false;
    let capturedDiscardData = null;

    setState((prev) => {
      if (prev.gamePhase !== 'playing' || prev.discardsRemaining <= 0) {
        return prev;
      }

      if (!prev.discardSelectionTileIds.length) {
        return prev;
      }

      const selectedSet = new Set(prev.discardSelectionTileIds);
      const selectedTiles = prev.rackTiles.filter((tile) => selectedSet.has(tile.id));
      if (!selectedTiles.length) {
        return { ...prev, isDiscardSelecting: false, discardSelectionTileIds: [] };
      }

      const remainingRackTiles = prev.rackTiles.filter((tile) => !selectedSet.has(tile.id));
      const drawn = drawTiles(prev.bagTiles, selectedTiles.length);
      discardApplied = true;
      capturedDiscardData = {
        count: selectedTiles.length,
        discardedLetters: selectedTiles.map((t) => t.letter),
        discardedTiles: selectedTiles.map((t) => ({ letter: t.letter, modifiers: t.modifiers })),
        discardsRemainingAfter: Math.max(0, prev.discardsRemaining - 1),
        roundIndex: prev.roundIndex,
      };

      return {
        ...prev,
        rackTiles: [...remainingRackTiles, ...drawn.drawn],
        bagTiles: [...drawn.bag, ...selectedTiles],
        discardsRemaining: Math.max(0, prev.discardsRemaining - 1),
        isDiscardSelecting: false,
        discardSelectionTileIds: [],
      };
    });

    if (discardApplied) {
      playDiscardConfirmSound();
      triggerDiscardConfirmHaptic();
      trackDiscardStats(capturedDiscardData);
      setSubmitMessage('Tiles discarded and redrawn.');
      return true;
    }

    setSubmitMessage('Select one or more tiles to discard.');
    return false;
  }, [setState, setSubmitMessage]);

  const continueToShop = React.useCallback(() => {
    let shopVisited = false;
    let capturedShopData = null;

    setState((prev) => {
      if (prev.gamePhase !== 'round-complete') {
        return prev;
      }

      const bagWithRemainingTiles = [
        ...prev.bagTiles,
        ...prev.rackTiles,
        ...Object.values(prev.stagedBoard),
      ];
      const shopOptions = drawShopOptions({
        roundIndex: prev.roundIndex,
        bagTiles: bagWithRemainingTiles,
        count: getShopOptionCount(prev.runSettings),
        rarityChances: getShopRarityChancesWithEffects(prev.runSettings, prev.activeGameEffects),
        runSettings: prev.runSettings,
      });
      const earnedShopPicks = Math.max(
        1,
        getShopPicksWithEffects(prev.runSettings, prev.activeGameEffects)
      );
      const shopRerollsRemaining = getShopRerollsWithEffects(prev.runSettings, prev.activeGameEffects);
      shopVisited = true;
      capturedShopData = {
        roundIndex: prev.roundIndex,
        earnedShopPicks,
        shopRerollsRemaining,
        shopOptions,
      };

      return {
        ...prev,
        stagedBoard: {},
        rackTiles: [],
        bagTiles: bagWithRemainingTiles,
        submitsRemaining: 0,
        discardsRemaining: 0,
        isDiscardSelecting: false,
        discardSelectionTileIds: [],
        shopPicksRemaining: earnedShopPicks,
        shopRerollsRemaining,
        shopOptions,
        selectedShopOptionId: null,
        gamePhase: 'shop',
      };
    });

    if (shopVisited) {
      trackShopVisit(capturedShopData);
    }
  }, [setState]);

  return {
    placeRackTile,
    moveStagedTile,
    returnStagedTileToRack,
    returnAllStagedTiles,
    reorderRackTile,
    submitMove,
    startDiscardSelection,
    cancelDiscardSelection,
    toggleDiscardTileSelection,
    confirmDiscardSelection,
    continueToShop,
  };
};
